import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Globe2, Languages, ArrowRight, Lock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Two-path chooser shown when a locked country is tapped. Picking a path calls
// onPick(campaignKey, displayName) so the parent can open the shared InvestModal.
export default function CountryInvestModal({ country, onClose, onPick }) {
  const { language } = useLanguage();
  const L = (lv, en, ru) => (language === 'lv' ? lv : language === 'ru' ? (ru ?? en) : en);
  const open = !!country;

  const paths = country ? [
    {
      key: country.regionCampaign,
      icon: Globe2,
      title: L('Atbloķēt šo valsti', 'Unlock this country', 'Разблокировать страну'),
      desc: L(`Palīdzi atvērt KoLV ${country.name} tirgū.`, `Help open the KoLV marketplace in ${country.name}.`, `Помогите открыть маркетплейс KoLV в стране ${country.name}.`),
      accent: true,
      testid: 'country-invest-unlock',
    },
    {
      key: country.langCampaign,
      icon: Languages,
      title: L('Atbalstīt valodas tulkojumu', 'Support the language translation', 'Поддержать перевод языка'),
      desc: L('Finansē pilnu lietotnes tulkojumu šai valodai.', 'Fund a full app translation for this language.', 'Профинансируйте полный перевод приложения на этот язык.'),
      accent: false,
      testid: 'country-invest-language',
    },
  ] : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="bg-[#0F1310] border-white/10 text-white max-w-md" data-testid="country-invest-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-2xl" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <span className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Lock className="w-4 h-4 text-amber-400" />
            </span>
            {country?.flag} {country?.name}
          </DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            {L('Šī valsts vēl nav pieejama. Izvēlies, kā vēlies palīdzēt to atvērt.',
               'This country is not available yet. Choose how you want to help open it.',
               'Эта страна пока недоступна. Выберите, как хотите помочь её открыть.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-1">
          {paths.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => onPick && onPick(p.key, country.name)}
                data-testid={p.testid}
                className={`w-full text-left p-5 rounded-2xl border flex items-start gap-4 transition-colors group ${
                  p.accent
                    ? 'border-[#FF9F1C]/30 bg-gradient-to-br from-[#FF9F1C]/10 to-transparent hover:from-[#FF9F1C]/20'
                    : 'border-white/10 bg-white/5 hover:bg-white/[0.09]'
                }`}
              >
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${p.accent ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-white/80'}`}>
                  <Icon className="w-5 h-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{p.title}</span>
                  <span className="block text-white/50 text-xs mt-0.5">{p.desc}</span>
                </span>
                <ArrowRight className="w-4 h-4 text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-1" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
